import { PaziaFormButton, PaziaSelect, PaziaInput, PaziaButton, PaziaDateSelect } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES } from "consts";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { useToaster } from "utils";
import { ModalPadding } from "../Modals/ModalPadding";
import { ModalHeader } from "../Modals/ModalHeader";
import { courseRegistrationApi, TrancheMapping } from "api/registrations";
import { useCoursesOptions } from "api/hooks";
import { useCourseTranches } from "api/hooks/useCourseTranches";
import { useUserPayments } from "api/hooks/useUserPayments";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS } from "consts";
import { CourseTrancheApi } from "api/types";

interface MoveUserToCourseModalProps {
  onCloseRequest: () => void;
  currentCourse: number;
  registrationId: number | null;
  userId: number;
}

export const MoveUserToCourseModal = ({
  onCloseRequest,
  currentCourse,
  registrationId,
  userId,
}: MoveUserToCourseModalProps) => {
  const { showSuccess, showError } = useToaster();
  const { options: courseOptions } = useCoursesOptions();
  const errorSectionRef = React.useRef<HTMLDivElement>(null);

  const methods = useForm<Values>({
    resolver: yupResolver(schema),
    defaultValues: {
      targetCourseId: "",
      newTotalPrice: 0,
      trancheMappings: [],
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const targetCourseId = watch("targetCourseId");
  const trancheMappings = watch("trancheMappings") || [];
  const { data: targetCourseTranches } = useCourseTranches(targetCourseId);
  const { data: userPayments } = useUserPayments(userId, currentCourse);

  // Update tranches when target course changes
  React.useEffect(() => {
    if (targetCourseTranches && targetCourseTranches.length > 0 && targetCourseId) {
      const newTranches = targetCourseTranches.map((tranche: CourseTrancheApi) => ({
        targetCourseTrancheId: tranche.id,
        amount: tranche.price,
        discountedAmount: tranche.price,
        name: tranche.name,
        dueDate: tranche.dueDate ? new Date(tranche.dueDate).toISOString().split("T")[0] : "",
        status: REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID.toString(),
        paymentId: null,
      }));

      setValue("trancheMappings", newTranches);

      // Calculate total price
      const totalPrice = newTranches.reduce((sum: number, tranche: any) => sum + tranche.discountedAmount, 0);
      setValue("newTotalPrice", totalPrice);
    }
  }, [targetCourseTranches, targetCourseId, setValue]);

  const onSubmit = async (formData: Values) => {
    if (!registrationId) return;

    try {
      await courseRegistrationApi.moveEnhanced(currentCourse, registrationId, {
        targetCourseId: Number(formData.targetCourseId),
        newTotalPrice: formData.newTotalPrice,
        trancheMappings: (formData.trancheMappings || []).map((mapping) => {
          const mappedDueDate = new Date(mapping.dueDate);
          mappedDueDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
          const tranche = {
            ...mapping,
            status: +mapping.status,
            paymentId: mapping.paymentId ?? null,
            dueDate: mappedDueDate,
          };

          return tranche;
        }),
      });
      onCloseRequest();
      showSuccess("Użytkownik został przeniesiony");
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas przenoszenia użytkownika");
    }
  };

  const onError = (errors: any) => {
    // Scroll to error section when validation fails
    if (errorSectionRef.current) {
      errorSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const availablePayments = userPayments?.data?.payments || [];

  // Check if there are any errors
  const hasErrors = Object.keys(errors).length > 0;

  const resolveErrorMessage = (error: any, index?: number, field?: string) => {
    const errMessage = (error as any)?.message;
    if (errMessage?.includes("must be a `number`")) return "musi być liczbą";
    return (error as any)?.message.includes("required") ? "jest wymaganym polem" : (error as any)?.message;
  };
  return (
    <PaziaModal
      id="move-user-enhanced-modal"
      onCloseRequest={onCloseRequest}
      isOpen={registrationId != null}
      size="5xl"
    >
      {registrationId && (
        <div className="max-h-[90vh] overflow-y-auto">
          <ModalPadding>
            <ModalHeader>Przenieś użytkownika na inny kurs</ModalHeader>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
                {/* Error Messages */}
                {hasErrors && (
                  <div ref={errorSectionRef} className="rounded-md border border-red-200 bg-red-50 p-4 dark:bg-red-500/10 dark:border-red-500/30">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Błędy walidacji</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc space-y-1 pl-5">
                            {errors.targetCourseId && <li>{errors.targetCourseId.message}</li>}
                            {errors.newTotalPrice && <li>{errors.newTotalPrice.message}</li>}
                            {errors.trancheMappings &&
                              Array.isArray(errors.trancheMappings) &&
                              errors.trancheMappings.map((trancheError, index) => {
                                if (trancheError) {
                                  return Object.entries(trancheError).map(([field, error]) => (
                                    <li key={`${index}-${field}`}>
                                      Transza {index + 1} -{" "}
                                      {field
                                        .replace("dueDate", "Termin płatności")
                                        .replace("amount", "Kwota")
                                        .replace("name", "Nazwa")
                                        .replace("discountedAmount", "Kwota po rabacie")
                                        .replace("status", "Status")
                                        .replace("paymentId", "Przypisana płatność")}{" "}
                                      {resolveErrorMessage(error, index, field)}
                                    </li>
                                  ));
                                }
                                return null;
                              })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Course Selection */}
                <div>
                  <PaziaSelect
                    name="targetCourseId"
                    label="Wybierz kurs docelowy"
                    required
                    options={courseOptions?.filter((opt) => opt.value !== currentCourse.toString()) || []}
                    placeholder="Wybierz kurs..."
                  />
                </div>

                {/* Total Price */}
                {targetCourseId && (
                  <div>
                    <PaziaInput
                      name="newTotalPrice"
                      required
                      label="Całkowita cena kursu"
                      placeholder="0.00"
                      {...({ step: 0.01, min: 0 } as any)}
                    />
                  </div>
                )}

                {/* Tranche Mappings */}
                {targetCourseId && trancheMappings.length > 0 && (
                  <div>
                    <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-slate-100">Transze i płatności</h3>

                    <div className="space-y-4">
                      {trancheMappings.map((tranche: any, index: number) => (
                        <div key={index} className="rounded-lg border border-gray-200 p-4 dark:border-slate-700">
                          <div className="grid grid-cols-2 gap-4">
                            {/* Tranche Name */}
                            <div>
                              <PaziaInput name={`trancheMappings.${index}.name`} label="Nazwa transzy" required />
                            </div>

                            {/* Amount */}
                            <div>
                              <PaziaInput
                                name={`trancheMappings.${index}.amount`}
                                label="Kwota"
                                required
                                placeholder="0.00"
                                {...({ step: 0.01, min: 0 } as any)}
                              />
                            </div>

                            {/* Discounted Amount */}
                            <div>
                              <PaziaInput
                                name={`trancheMappings.${index}.discountedAmount`}
                                label="Kwota po rabacie"
                                placeholder="0.00"
                                required
                                {...({ step: 0.01, min: 0 } as any)}
                              />
                            </div>

                            {/* Due Date */}
                            <div>
                              <PaziaDateSelect
                                name={`trancheMappings.${index}.dueDate`}
                                required
                                label="Termin płatności"
                              />
                            </div>

                            {/* Status */}
                            <div>
                              <PaziaSelect
                                name={`trancheMappings.${index}.status`}
                                required
                                label="Status"
                                options={REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS}
                              />
                            </div>

                            {/* Payment Assignment */}
                            <div>
                              <PaziaSelect
                                name={`trancheMappings.${index}.paymentId`}
                                label="Przypisana płatność"
                                options={[
                                  { value: "", label: "Brak przypisanej płatności" },
                                  ...availablePayments.map((payment) => ({
                                    value: payment.id.toString(),
                                    label: `Płatność #${payment.id} - ${payment.amount} PLN`,
                                  })),
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:bg-orange-500/10 dark:border-orange-500/30">
                  <span className="font-medium text-orange-800">Informacja o przeniesieniu:</span>
                  <ul className="mt-2 text-sm text-orange-700">
                    <li>1. Użytkownik zostanie wykreślony z listy uczestników kursu źródłowego.</li>
                    <li>2. Użytkownik zostanie dodany do listy uczestników kursu docelowego.</li>
                    <li>3. Płatności zostaną przypisane według ustawień powyżej.</li>
                    <li>4. Struktura transz zostanie zaktualizowana zgodnie z nowym kursem.</li>
                  </ul>
                </div>

                <div className="flex justify-end gap-2">
                  <PaziaButton btnType="button" type="tertiary" onClick={onCloseRequest} title="Anuluj" />
                  <PaziaFormButton
                    title="Przenieś użytkownika"
                    disabled={!targetCourseId || trancheMappings.length === 0}
                  />
                </div>
              </form>
            </FormProvider>
          </ModalPadding>
        </div>
      )}
    </PaziaModal>
  );
};

const schema = yup
  .object({
    targetCourseId: yup.string().required("Wybierz kurs docelowy"),
    newTotalPrice: yup.number().min(0, "Cena musi być większa lub równa 0").required(),
    trancheMappings: yup.array().of(
      yup.object({
        targetCourseTrancheId: yup.number().required(),
        amount: yup.number().min(0).required(),
        discountedAmount: yup.number().min(0).required(),
        name: yup.string().required(),
        dueDate: yup.string().required(),
        status: yup.string().required(),
        paymentId: yup.number().nullable(),
      }),
    ),
  })
  .required();

type Values = yup.InferType<typeof schema>;
